import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Download, Loader2, Trash2, FileText } from "lucide-react";
import { api, errMsg, downloadBlob } from "@/lib/api";
import { OrderForm } from "@/components/admin/OrderForm";
import { StatusBadge } from "@/components/Brand";
import { Timeline } from "@/components/Timeline";
import { FileGallery } from "@/components/FileGallery";
import { IssuesList } from "@/components/IssuesList";
import { STATUS_FLOW, buildWhatsAppUrl, fmtDateTime } from "@/lib/helpers";
import { t } from "@/lib/i18n";
import { useOnNotify } from "@/components/admin/NotificationBell";

const FORM_KEYS = ["reference", "partner", "source", "stops", "cargo", "weight", "loading_meters", "pallets", "vehicle", "driver_notes", "price", "currency", "driver_id"];
const pick = (o) => Object.fromEntries(FORM_KEYS.map((k) => [k, o[k]]));

function WhatsAppCard({ order, driver, dirty, onSent }) {
  if (!driver) return <div className="panel text-sm text-slate-400" data-testid="whatsapp-no-driver">Alege un șofer și salvează ca să poți trimite comanda pe WhatsApp.</div>;
  const disabled = dirty;
  return (
    <div className="panel" data-testid="whatsapp-card">
      <div className="eyebrow">Trimite la șofer</div>
      <div className="mt-2 font-display text-2xl font-bold uppercase">{driver.name}</div>
      <div className="font-mono text-xs text-slate-400">+{driver.phone} · mesaj în {driver.language.toUpperCase()}</div>
      <a
        href={disabled ? undefined : buildWhatsAppUrl(order, driver)}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => { if (disabled) { e.preventDefault(); toast.error("Salvează modificările înainte de trimitere"); } else onSent(); }}
        data-testid="whatsapp-send-btn"
        className={`mt-4 flex h-12 items-center justify-center gap-2 rounded-xl font-semibold text-[#062b14] transition-colors ${disabled ? "bg-[#25D366]/40" : "bg-[#25D366] hover:bg-[#1EBE5D]"}`}
      >
        <Send className="h-4 w-4" /> Trimite pe WhatsApp
      </a>
      <p className="mt-2 text-xs text-slate-500" data-testid="whatsapp-sent-at">
        {order.whatsapp_sent_at ? `Ultima trimitere: ${fmtDateTime(order.whatsapp_sent_at)}` : "Fără preț. Include adresele, link-uri Google Maps și link-ul aplicației."}
      </p>
    </div>
  );
}

function StatusCard({ order, onChange }) {
  return (
    <div className="panel space-y-4">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Status</div>
        <StatusBadge status={order.status} testId="order-detail-status" />
      </div>
      <select value={order.status} onChange={(e) => onChange(e.target.value)} data-testid="admin-status-select" className="h-10 w-full rounded-md border border-white/10 bg-[#0A0E17] px-3 text-sm">
        {STATUS_FLOW.map((s) => <option key={s} value={s}>{t("ro", `status_${s}`)}</option>)}
      </select>
      <Timeline history={order.status_history} />
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [zipping, setZipping] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
    setForm(pick(data));
  }, [id]);

  useEffect(() => {
    load().catch(() => toast.error("Comanda nu a fost găsită"));
    api.get("/drivers").then((r) => setDrivers(r.data));
  }, [load]);

  const onNotify = useCallback(
    async (e) => {
      if (!e.detail.some((n) => n.order_id === id)) return;
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      setForm((f) => (f && JSON.stringify(f) !== JSON.stringify(pick(order || data)) ? f : pick(data)));
    },
    [id, order],
  );
  useOnNotify(onNotify);

  if (!order || !form) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  const dirty = JSON.stringify(form) !== JSON.stringify(pick(order));
  const driver = drivers.find((d) => d.id === order.driver_id);
  const docs = order.files.filter((f) => ["cmr", "lieferschein", "other"].includes(f.kind));
  const pdf = order.files.find((f) => f.kind === "pdf");

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/orders/${id}`, form);
      await load();
      toast.success("Salvat");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const setStatus = async (status) => {
    try {
      await api.post(`/orders/${id}/status`, { status });
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm("Ștergi definitiv comanda și documentele ei?")) return;
    await api.delete(`/orders/${id}`);
    toast.success("Comandă ștearsă");
    nav("/admin");
  };
  const zip = async () => {
    setZipping(true);
    try {
      await downloadBlob(`/orders/${id}/documents.zip`, `${order.reference || "comanda"}_documente.zip`);
    } catch {
      toast.error("Nu am putut descărca arhiva");
    } finally {
      setZipping(false);
    }
  };

  return (
    <div>
      <button onClick={() => nav("/admin")} data-testid="back-to-orders-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Comenzi</button>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-4xl font-extrabold uppercase sm:text-5xl" data-testid="order-detail-title">{order.reference || "Fără referință"}</h1>
        <StatusBadge status={order.status} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7 xl:col-span-8">
          <OrderForm value={form} onChange={setForm} drivers={drivers} />
          <div className="sticky bottom-0 mt-6 flex items-center gap-3 border-t border-white/10 bg-[#0A0E17]/90 py-4 backdrop-blur">
            <button onClick={remove} data-testid="delete-order-btn" className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /> Șterge</button>
            <button onClick={save} disabled={!dirty || saving} data-testid="update-order-btn" className="ml-auto inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {dirty ? "Salvează modificările" : "Salvat"}
            </button>
          </div>
        </div>

        <aside className="space-y-6 lg:col-span-5 xl:col-span-4">
          <WhatsAppCard order={order} driver={driver} dirty={dirty} onSent={() => api.post(`/orders/${id}/sent`).then(load)} />
          <StatusCard order={order} onChange={setStatus} />
          <div className="panel space-y-4">
            <div className="flex items-center justify-between">
              <div className="eyebrow">Documente CMR / Lieferschein ({docs.length})</div>
              <button onClick={zip} disabled={!docs.length || zipping} data-testid="download-zip-btn" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs hover:bg-white/5 disabled:opacity-40">
                {zipping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} ZIP
              </button>
            </div>
            <FileGallery files={docs} empty="Șoferul nu a încărcat încă poze." />
            {pdf && (
              <button onClick={() => downloadBlob(`/files/${pdf.id}`, pdf.filename)} data-testid="download-source-pdf-btn" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
                <FileText className="h-3.5 w-3.5" /> PDF original de la bursă (doar birou)
              </button>
            )}
          </div>
          {order.issues.length > 0 && (
            <div className="panel space-y-3">
              <div className="eyebrow text-red-300">Probleme raportate</div>
              <IssuesList issues={order.issues} files={order.files} onResolved={load} admin />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
